import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import SourceService from './sourceService';
import Switch from '@mui/material/Switch';
import SourceCreate from './sourceCreate';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ButtonGroup from '@mui/material/ButtonGroup';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FilterListIcon from '@mui/icons-material/FilterList';
import Badge from '@mui/material/Badge';
import useActivityStore from '../../stores/activityStore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const sourceservice = new SourceService;

const SourceDeleteButton = (props) => {
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);

  const deletesource = () => {
    setLoading(true);
    sourceservice.deleteSource(props.id)
      .then((response) => {
        setLoading(false);
        window.location.reload(false); // refresh page
      })
      .catch((error) => {
        setLoading(false);
        setConfirmation(false);
        alert('Failed to delete source: ' + (error.response?.data?.message || error.message));
      })
  }

  function cancel() {
    setConfirmation(false);
  }

  function confirm() {
    setConfirmation(true);
  }

  if (loading) {
    return (<CircularProgress size="1rem" color="inherit" />);
  }
  else if (confirmation) {
    return (
      <ButtonGroup variant="contained" aria-label="outlined primary button group">
        <Button color="error" onClick={deletesource}>Confirm</Button>
        <Button onClick={cancel}><HighlightOffIcon /></Button>
      </ButtonGroup>
    );
  }
  else {
    return (
      <Button variant="contained" color="error" onClick={confirm} disabled={loading}>
        Delete
      </Button>
    );
  }

}


const SourceRow = (props) => {
  const navigate = useNavigate();
  const [source, setSource] = useState([]);
  const [activating, setActivating] = useState(false);

  // Get real-time mount status from Zustand store
  const getMountConnectionCount = useActivityStore((state) => state.getMountConnectionCount);
  const isMountActive = useActivityStore((state) => state.isMountActive);

  useEffect(() => {
    setSource(props.source);
  }, [props.source]);

  function toggle() {
    setActivating(true);
    if (source.active) {
      sourceservice.disableSource(source.id).then((response) => {
        if (response.result) {
          setSource(prevState => ({
            ...prevState,
            active: false
          }));
          setActivating(false);
        }
      })
        .catch((error) => {
          setActivating(false);
        });
    }
    else {
      sourceservice.enableSource(source.id).then((response) => {
        if (response.result) {
          setSource(prevState => ({
            ...prevState,
            active: true
          }));
          setActivating(false);
        }
      })
        .catch((error) => {
          setActivating(false);
        });
    }
  }

  return (
    <TableRow
      key={"source-" + source.id}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={source.name} color="primary" />
          {isMountActive(source.name) && getMountConnectionCount(source.name) > 0 && (
            <Badge badgeContent={getMountConnectionCount(source.name)} color="success">
              <FiberManualRecordIcon color="success" fontSize="small" />
            </Badge>
          )}
        </Stack>
      </TableCell>
      <TableCell align="left">{source.password}&nbsp;<IconButton onClick={() => { navigator.clipboard.writeText(source.password) }}><ContentPasteIcon /></IconButton></TableCell>
      <TableCell align="left">{activating ? <CircularProgress size="1rem" color="inherit" /> : <Switch checked={source.active} onClick={toggle} color="success" />}</TableCell>
      <TableCell align="left">
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={() => navigate("/caster/mountpoint/" + source.id)}>
            Edit
          </Button>
          <SourceDeleteButton id={source.id} />
        </Stack>
      </TableCell>
    </TableRow>



  );
}

export default function SourceList() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchText, statusFilter, rows]);

  const fetchData = () => {
    setLoading(true);
    sourceservice.getSources().then((response) => {
      setRows(response);
      setLoading(false);
    });
  };

  const applyFilters = () => {
    let filtered = [...rows];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(row =>
        row.name?.toLowerCase().includes(searchLower) ||
        row.password?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(row => row.active === true);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(row => row.active === false);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (order === 'asc') {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });

    setFilteredRows(filtered);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    applyFilters();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <React.Fragment>
      <h1 className="page-title"><span className="fw-semi-bold">Push-In Sources</span></h1>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Search sources..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
            startAdornment={<FilterListIcon sx={{ ml: 1, mr: -0.5 }} />}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={fetchData} title="Refresh">
          <RefreshIcon />
        </IconButton>
        <Chip
          label={`${filteredRows.length} ${filteredRows.length === 1 ? 'source' : 'sources'}`}
          size="small"
        />
      </Stack>

      <Paper elevation={1}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="sources table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Mountpoint
                  </TableSortLabel>
                </TableCell>
                <TableCell>Password</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'active'}
                    direction={orderBy === 'active' ? order : 'asc'}
                    onClick={() => handleRequestSort('active')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    {searchText || statusFilter !== 'all' ? 'No sources found matching your filters' : 'No push-in sources'}
                  </TableCell>
                </TableRow>
              ) : (
                displayRows.map((row) => <SourceRow key={row.id} source={row} />)
              )}
            </TableBody>
          </Table>
          {loading && <LinearProgress />}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {!rows.length && !showCreateForm && (
        <Stack spacing={2} direction="row" alignItems="center">
          <Paper style={{ margin: "auto", marginTop: "2em", marginBottom: "2em", padding: "1em", width: "50%" }} >
            <Stack spacing={2} direction="column" alignItems="center">
              <p>No existing mountpoints</p>
              <Button variant="contained" onClick={() => setShowCreateForm(true)}>+ New Mountpoint</Button>
            </Stack>
          </Paper>
        </Stack>
      )}
      {showCreateForm &&
        <Stack direction="column" alignItems="center">
          <Paper style={{ marginTop: "2em", marginBottom: "2em", padding: "1em", minWidth: "15vw", maxWidth: "50vw" }} >
            <h3>Add Push-In Source</h3>
            <p>Push-In Sources allow other stations to push a correction stream into this base on a specified mountpoint. Push-In sources are also used to stream GNSS receivers connected by USB/serial.</p>
            <Stack spacing={2} direction="column" alignItems="center">
              <p>Adding a new push in source provides a mountpoint to push data to. It doesn't establish any connections. The mountpoint will be avialable once a source begins push data to the mountpoint.</p>
              <p>Use the form below to add a new mountpoint for push-in sources. A password is required to authenticate sources. If left blank a password will be generated automatically.</p>
              <SourceCreate />
            </Stack>
          </Paper>
        </Stack>}
      {rows.length > 0 && !showCreateForm && <Button variant="contained" onClick={() => setShowCreateForm(true)}>+ New Source</Button>}
    </React.Fragment >
  );
}
